import { useState } from "react"

import { Alert, Flex, Modal, Typography } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"
import { connect, useSelector } from "react-redux"

import Button from "../../../Components/Button"
import Icon from "../../../Icon"
import IntegrationCard from "../../IntegrationsPage/components/Card"
import IntegrationRequestForm from "../../IntegrationsPage/components/IntegrationRequestForm"
import { requestNewIntegration } from "../../User/redux/user.thunks"
import { MCP_CARDS } from "./guides"
import McpGuideModal from "./McpGuideModal"
import { useMcpConnections } from "./useMcpConnections"

import "./styles.less"

const { Text } = Typography

// The "Integration missing?" tile reuses the exact request card from the Integrate
// page (/integrations/processors) so it's identical, and opens the same
// request-integration popup (IntegrationRequestForm).
const REQUEST_CARD = {
  id: "mcp-missing",
  isRequestIntegration: true,
  service_name: "missing",
  title: "Integration missing?",
  description:
    "Let our team know which integration you are missing and we will add it as soon as possible.",
  is_connected: false,
  isDisabled: false,
  withoutPermission: true
}

// MCP integration directory: a grid of AI tools, each opening a step-by-step setup
// guide (McpGuideModal).
const McpIntegrations = ({ theme, saveFetching, requestNewIntegration }) => {
  const { t } = useTranslation()
  const isMobile = useSelector((state) => state.nav.isMobile)
  const [guideKey, setGuideKey] = useState(null)
  const [requestOpen, setRequestOpen] = useState(false)
  const { connectedKeys, disconnectingKey, disconnect } = useMcpConnections()

  const closeRequest = () => setRequestOpen(false)
  const handleRequest = (data) => {
    requestNewIntegration(data)
    closeRequest()
  }

  return (
    <>
      <Alert
        type="warning"
        className="fullscreen-alert"
        style={{ marginBottom: 16 }}
        showIcon
        icon={<Icon type={"bell_alarm"} />}
        message="Only account owners can currently access business data and alerts through MCP. Support for delegated access will be added in upcoming versions."
      />
      <Flex wrap="wrap" gap={16} className="mcp-card-grid">
        {MCP_CARDS.map((card) =>
          card.missing ? (
            <IntegrationCard
              key={card.key}
              data={REQUEST_CARD}
              theme={theme}
              onClick={() => setRequestOpen(true)}
            />
          ) : (
            (() => {
              const isConnected = connectedKeys.has(card.key)

              return (
                <div
                  key={card.key}
                  className={cls("mcp-card", { "mcp-card_connected": isConnected })}
                >
                  <div className="mcp-card-icon">
                    {card.icon ? (
                      <img
                        src={theme === "dark" && card.icon_dark ? card.icon_dark : card.icon}
                        alt={card.title}
                      />
                    ) : (
                      <Icon type="plugin_connect" />
                    )}
                  </div>
                  <div className="mcp-card-info">
                    <Flex align="center" gap={8} wrap>
                      <Text className="mcp-card-title">{t(card.title)}</Text>
                      {isConnected && <span className="mcp-card-badge">{t("Connected")}</span>}
                    </Flex>
                    <Text type="secondary" className="mcp-card-desc">
                      {t(card.desc)}
                    </Text>
                  </div>
                  {isConnected ? (
                    <Button
                      size="large"
                      className="flex center mcp-card-btn"
                      icon={<Icon type="circle_x" style={{ fontSize: 20 }} />}
                      text={t("Disconnect")}
                      loading={disconnectingKey === card.key}
                      onClick={() => disconnect(card.key)}
                    />
                  ) : (
                    <Button
                      size="large"
                      className="flex center mcp-card-btn"
                      icon={<Icon type="plugin_connect" style={{ fontSize: 20 }} />}
                      text={t("Connect")}
                      onClick={() => setGuideKey(card.guide)}
                    />
                  )}
                </div>
              )
            })()
          )
        )}
      </Flex>

      <McpGuideModal guideKey={guideKey} onClose={() => setGuideKey(null)} />

      {/* Same request popup as /integrations/processors: identical antd Modal
          config (title, close icon, clear-bg full-width-header) + IntegrationRequestForm. */}
      <Modal
        open={requestOpen}
        centered={!isMobile}
        width={442}
        title={t("Integration request")}
        footer={null}
        closeIcon={<Icon type="cross_small" style={{ color: "#7A7A83" }} />}
        closable="true"
        onCancel={closeRequest}
        destroyOnClose
        classNames={{ content: "clear-bg full-width-header" }}
        className="mcp-request-modal"
      >
        <div>
          <Text type="secondary" style={{ display: "inline-block", marginBottom: 16 }}>
            {t(
              "Let our team know which integration you are missing and we will add it as soon as possible."
            )}
          </Text>
          <IntegrationRequestForm
            onClose={closeRequest}
            onSubmit={handleRequest}
            loading={saveFetching}
          />
        </div>
      </Modal>
    </>
  )
}

const mapStateToProps = (state) => ({
  theme: state.nav.theme,
  saveFetching: state.user.saveFetching
})

const mapDispatchToProps = (dispatch) => ({
  requestNewIntegration: (data) => dispatch(requestNewIntegration(data))
})

export default connect(mapStateToProps, mapDispatchToProps)(McpIntegrations)
